import React, { useState, useEffect } from "react";
import { Copy, Check, Code2, FileCode, Globe, Shield, ExternalLink, Headset } from "lucide-react";
import { adminApi, getActiveAgent } from "@/lib/api";
import { toast } from "sonner";

const WIDGET_HOST =
    process.env.REACT_APP_WIDGET_HOST ||
    process.env.REACT_APP_BACKEND_URL ||
    "https://orchid-cust-bot.fundalzone.com";
const SCRIPT_TAG = `<script async src="${WIDGET_HOST}/api/widget.js"></script>`;
const DEMO_URL = "/demo";

const STEPS = [
    {
        n: 1,
        title: "Add the script tag",
        body:
            "Paste this single line into your website template — ideally just before the closing </body> tag, or in the global footer include used by every page.",
        code: SCRIPT_TAG,
    },
    {
        n: 2,
        title: "Verify it loads",
        body:
            "Visit any page on orchidhotel.com after deploying. Within 4 seconds you'll see a small greeting toast in the bottom-right, and an emerald floating chat bubble.",
    },
    {
        n: 3,
        title: "That's it. The Concierge is live.",
        body:
            "Every visitor interaction is now logged into the admin console. Upload property guides and policy documents under Knowledge Base so the AI can answer guest questions intelligently.",
    },
];

export default function DeploymentGuide() {
    const [copied, setCopied] = useState(false);
    const [lc, setLc] = useState(null);
    const [lcError, setLcError] = useState(false);
    const [lcSaving, setLcSaving] = useState(false);

    useEffect(() => {
        adminApi
            .get("/api/admin/settings/live-chat")
            .then((r) => { setLc(r.data); setLcError(false); })
            .catch(() => { setLc(null); setLcError(true); });
    }, []);

    const toggleLiveChat = async () => {
        if (!lc || lcSaving) return;
        const next = !lc.live_chat_enabled;
        setLcSaving(true);
        try {
            await adminApi.put("/api/admin/settings/live-chat", { enabled: next });
            setLc({ ...lc, live_chat_enabled: next });
            toast.success(`Live chat ${next ? "enabled" : "disabled"} for ${lc.name}`);
        } catch (e) {
            toast.error("Could not update live chat setting");
        } finally {
            setLcSaving(false);
        }
    };

    const copy = () => {
        navigator.clipboard.writeText(SCRIPT_TAG);
        setCopied(true);
        setTimeout(() => setCopied(false), 1800);
    };

    return (
        <div className="p-10 max-w-4xl">
            <div className="mb-10">
                <div className="text-xs uppercase tracking-[0.25em] text-stone-500 mb-2">
                    For the Orchid Hotels Web Team
                </div>
                <h1 className="font-heading text-5xl text-orchid-700">Deployment Guide</h1>
                <p className="text-stone-600 mt-3 max-w-2xl">
                    One line of code embeds the entire Concierge experience on orchidhotel.com.
                    No build changes, no dependencies, no rebuilds — the widget loads
                    asynchronously and stays out of the way of your existing UI.
                </p>
                <a
                    href={DEMO_URL}
                    target="_blank"
                    rel="noreferrer"
                    data-testid="open-demo"
                    className="mt-5 inline-flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-orchid-950 font-medium px-5 py-2.5 rounded-md transition-colors"
                >
                    <ExternalLink size={15} /> Open the live demo page
                </a>
            </div>

            {/* Hero embed snippet */}
            <div
                className="bg-orchid-950 text-ivory rounded-lg p-6 mb-10 relative overflow-hidden"
                data-testid="embed-snippet"
            >
                <div
                    className="absolute inset-0 opacity-10"
                    style={{
                        backgroundImage:
                            "radial-gradient(circle at 30% 50%, #D4AF37 0%, transparent 40%)",
                    }}
                />
                <div className="relative">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-gold-400 mb-3">
                        <Code2 size={14} /> The Snippet
                    </div>
                    <pre className="font-mono text-sm overflow-x-auto p-4 bg-black/30 rounded-md border border-white/10">
                        <code>{SCRIPT_TAG}</code>
                    </pre>
                    <button
                        onClick={copy}
                        data-testid="copy-snippet"
                        className="mt-4 flex items-center gap-2 bg-gold-500 hover:bg-gold-600 text-orchid-950 font-medium px-4 py-2 rounded-md transition-colors"
                    >
                        {copied ? (
                            <>
                                <Check size={15} /> Copied!
                            </>
                        ) : (
                            <>
                                <Copy size={15} /> Copy snippet
                            </>
                        )}
                    </button>
                </div>
            </div>

            {/* Live Chat on/off (per active bot) */}
            {lc && (
                <div
                    className="bg-white border border-stone-200 rounded-lg p-6 mb-10 flex items-start justify-between gap-6"
                    data-testid="live-chat-toggle-card"
                >
                    <div className="flex gap-4">
                        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-sage-600/15 text-sage-700 flex items-center justify-center">
                            <Headset size={20} />
                        </div>
                        <div>
                            <h3 className="font-heading text-2xl text-orchid-700 mb-1">
                                Live Chat — {lc.name}
                            </h3>
                            <p className="text-sm text-stone-600 max-w-xl">
                                When <strong>ON</strong>, guests can tap <strong>“Live Agent”</strong> to be handed
                                off to a human team member. When <strong>OFF</strong>, the Live Agent button is
                                hidden and {lc.name} handles the whole conversation.
                            </p>
                            <p className="text-xs text-stone-500 mt-2">
                                Status:{" "}
                                <strong
                                    className={lc.live_chat_enabled ? "text-sage-700" : "text-stone-500"}
                                    data-testid="live-chat-status"
                                >
                                    {lc.live_chat_enabled ? "Live chat ON" : "Live chat OFF"}
                                </strong>
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={toggleLiveChat}
                        disabled={lcSaving}
                        role="switch"
                        aria-checked={lc.live_chat_enabled}
                        data-testid="live-chat-toggle"
                        className={`relative inline-flex h-7 w-12 flex-shrink-0 items-center rounded-full transition-colors disabled:opacity-60 ${
                            lc.live_chat_enabled ? "bg-sage-600" : "bg-stone-300"
                        }`}
                    >
                        <span
                            className={`inline-block h-6 w-6 transform rounded-full bg-white shadow transition-transform ${
                                lc.live_chat_enabled ? "translate-x-5" : "translate-x-0.5"
                            }`}
                        />
                    </button>
                </div>
            )}

            {lcError && (
                <div
                    className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg px-5 py-3 mb-10 text-sm"
                    data-testid="live-chat-load-error"
                >
                    Couldn’t load the Live Chat setting right now. Please refresh the page to try again.
                </div>
            )}

            {/* Steps */}
            <div className="space-y-5 mb-12">
                {STEPS.map((s) => (
                    <div
                        key={s.n}
                        className="bg-white border border-stone-200 rounded-lg p-6 flex gap-5"
                    >
                        <div className="flex-shrink-0 w-10 h-10 rounded-full bg-orchid-700 text-gold-500 flex items-center justify-center font-heading text-xl">
                            {s.n}
                        </div>
                        <div className="flex-1">
                            <h3 className="font-heading text-2xl text-orchid-700 mb-1">
                                {s.title}
                            </h3>
                            <p className="text-stone-600 leading-relaxed">{s.body}</p>
                            {s.code && (
                                <pre className="mt-3 font-mono text-xs bg-stone-50 border border-stone-200 rounded-md p-3 overflow-x-auto">
                                    <code>{s.code}</code>
                                </pre>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {/* What it does + How it works */}
            <div className="grid lg:grid-cols-2 gap-5 mb-12">
                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <Globe size={20} className="text-orchid-700 mb-3" />
                    <h3 className="font-heading text-2xl text-orchid-700 mb-2">
                        Where it loads
                    </h3>
                    <ul className="text-sm text-stone-700 space-y-1.5 list-disc pl-5">
                        <li>Bottom-right floating chat bubble</li>
                        <li>Auto-greets visitors after 4 seconds (configurable)</li>
                        <li>Opens into a 400×640 chat panel with menu + free-text</li>
                        <li>Mobile-friendly — adapts to full screen on small devices</li>
                    </ul>
                </div>
                <div className="bg-white border border-stone-200 rounded-lg p-6">
                    <Shield size={20} className="text-orchid-700 mb-3" />
                    <h3 className="font-heading text-2xl text-orchid-700 mb-2">
                        Security & Performance
                    </h3>
                    <ul className="text-sm text-stone-700 space-y-1.5 list-disc pl-5">
                        <li>Loaded asynchronously — zero impact on page-load metrics</li>
                        <li>Runs inside a sandboxed iframe — cannot interfere with site styles</li>
                        <li>No cookies set on the host domain</li>
                        <li>OTP / Petals data isolated to your Fundle backend</li>
                    </ul>
                </div>
            </div>

            {/* FAQ */}
            <div className="bg-white border border-stone-200 rounded-lg p-8">
                <h3 className="font-heading text-3xl text-orchid-700 mb-5">FAQ</h3>
                <div className="space-y-4 text-sm">
                    {[
                        {
                            q: "Will it conflict with our site's CSS or JS?",
                            a: "No. The widget runs inside an iframe and an isolated bubble container. None of its CSS escapes into your page.",
                        },
                        {
                            q: "Can we delay the auto-greet or disable it entirely?",
                            a: "Yes — go to Admin → Settings → Greeting & Tone, set the auto-greet delay to any value, or set it to a very large number to effectively disable it.",
                        },
                        {
                            q: "How do we update the welcome message or menu wording?",
                            a: "Welcome message is editable under Settings. Menu copy follows the flow document and updates require a small backend release — contact us.",
                        },
                        {
                            q: "How does the AI know about our properties?",
                            a: "Upload your property fact-sheets, FAQs, policy PDFs under Knowledge Base. The Concierge uses BM25 retrieval over the indexed chunks to ground every free-text answer.",
                        },
                    ].map((f) => (
                        <details
                            key={f.q}
                            className="group border-b border-stone-100 pb-3 last:border-0"
                        >
                            <summary className="cursor-pointer font-medium text-stone-900 list-none flex items-center justify-between">
                                {f.q}
                                <span className="text-stone-400 group-open:rotate-180 transition-transform">
                                    ▾
                                </span>
                            </summary>
                            <p className="mt-2 text-stone-600 leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
            </div>
        </div>
    );
}
